import type { Dictionary } from '@/i18n/dictionary'
import type { Locale } from '@/i18n/locales'
import { LOCALES } from '@/i18n/locales'

// Each locale is its own chunk, so a visitor only downloads the language they read.
const loaders: Record<Locale, () => Promise<Dictionary>> = {
  en: () => import('@/i18n/locales/en').then((module) => module.en),
  hr: () => import('@/i18n/locales/hr').then((module) => module.hr),
  de: () => import('@/i18n/locales/de').then((module) => module.de),
  it: () => import('@/i18n/locales/it').then((module) => module.it),
  fr: () => import('@/i18n/locales/fr').then((module) => module.fr),
  es: () => import('@/i18n/locales/es').then((module) => module.es),
  pl: () => import('@/i18n/locales/pl').then((module) => module.pl),
  cs: () => import('@/i18n/locales/cs').then((module) => module.cs),
  sk: () => import('@/i18n/locales/sk').then((module) => module.sk),
  hu: () => import('@/i18n/locales/hu').then((module) => module.hu),
  sl: () => import('@/i18n/locales/sl').then((module) => module.sl),
  sr: () => import('@/i18n/locales/sr').then((module) => module.sr),
}

const loaded = new Map<Locale, Dictionary>()

export const hasDictionary = (locale: Locale): boolean => loaded.has(locale)

export const loadDictionary = async (locale: Locale): Promise<Dictionary> => {
  const existing = loaded.get(locale)
  if (existing !== undefined) return existing

  const dictionary = await loaders[locale]()
  loaded.set(locale, dictionary)
  return dictionary
}

export const loadAllDictionaries = async (): Promise<void> => {
  await Promise.all(LOCALES.map(loadDictionary))
}

export const getDictionary = (locale: Locale): Dictionary => {
  const dictionary = loaded.get(locale)
  if (dictionary === undefined) throw new Error(`Dictionary "${locale}" has not been loaded`)
  return dictionary
}

export const interpolate = (template: string, values: Record<string, string | number>): string =>
  template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const value = values[key]
    return value === undefined ? match : String(value)
  })

export type { Dictionary } from '@/i18n/dictionary'
export * from '@/i18n/locales'
